import { Box, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import {
  NUMBERED_COLUMN_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
} from '@databases/utils/constants';
import React from 'react';

import { DEFAULT_SKELETON_HEIGHT } from './constants';

const TABLE_HEADER_ROW_HEIGHT = 40;
const TABLE_CELLS_ROW_HEIGHT = 48;
const NUM_SKELETON_ROWS = 3;
const NUM_SKELETON_COLUMNS = 3;

const tableContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: token('radius.small', '4px'),
  backgroundColor: 'elevation.surface',
  overflow: 'hidden',
  paddingLeft: 'space.600',
  paddingRight: 'space.600',
  position: 'relative',
});

const tableHeaderStyles = xcss({
  display: 'flex',
  backgroundColor: 'elevation.surface.sunken',
  border: `1px solid ${token('color.border')}`,
  borderRadius: token('radius.medium', '8px'),
  position: 'relative',
});

const tableHeaderCellStyles = xcss({
  padding: 'space.200',
  display: 'flex',
  alignItems: 'center',
  minHeight: `${TABLE_HEADER_ROW_HEIGHT}px`,
  flexShrink: 0,
  gap: 'space.100',
});

const numberedEntryFieldStyles = xcss({
  width: `${NUMBERED_COLUMN_WIDTH}px`,
  borderRight: `1px solid ${token('color.border')}`,
  borderTopLeftRadius: token('radius.medium', '8px'),
  borderBottomLeftRadius: token('radius.medium', '8px'),
});

const fieldStyles = xcss({
  minWidth: `${TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH}px`,
  flex: '1',
  borderRight: `1px solid ${token('color.border')}`,
});

const lastHeaderCellStyles = xcss({
  borderRight: 'none',
  borderTopRightRadius: token('radius.medium', '8px'),
  borderBottomRightRadius: token('radius.medium', '8px'),
});

const tableBodyStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: '0px solid transparent',
  borderLeft: `1px solid transparent`,
  borderRight: `1px solid transparent`,
  backgroundImage: `linear-gradient(to right, transparent 0%, ${token(
    'color.border'
  )} 2%, ${token('color.border')} 98%, transparent 100%)`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom',
  backgroundSize: '100% 1px',
});

const tableRowStyles = xcss({
  display: 'flex',
  minHeight: `${TABLE_CELLS_ROW_HEIGHT}px`,
});

const tableRowBorderStyles = xcss({
  borderBottom: '0px solid transparent',
  backgroundImage: `linear-gradient(to right, transparent 0%, ${token(
    'color.border'
  )} 2%, ${token('color.border')} 98%, transparent 100%)`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom',
  backgroundSize: '100% 1px',
});

const tableCellStyles = xcss({
  padding: 'space.200',
  display: 'flex',
  alignItems: 'center',
  minHeight: `${TABLE_CELLS_ROW_HEIGHT}px`,
  flexShrink: '0',
});

const numberedEntryFieldCellStyles = xcss({
  width: `${NUMBERED_COLUMN_WIDTH}px`,
  borderRight: `1px solid ${token('color.border')}`,
});

const fieldCellStyles = xcss({
  minWidth: `${TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH}px`,
  flex: '1',
  borderRight: `1px solid ${token('color.border')}`,
});

const lastCellStyles = xcss({
  borderRight: 'none',
});

const actionsContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: '0px solid transparent',
  backgroundImage: `linear-gradient(to right, transparent 0%, ${token(
    'color.border'
  )} 2%, ${token('color.border')} 98%, transparent 100%)`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom',
  backgroundSize: '100% 1px',
});

const addEntryButtonSkeletonStyles = xcss({
  height: '38px',
  borderRadius: token('radius.small', '4px'),
  padding: 'space.150',
});

const addFieldButtonContainerStyles = xcss({
  position: 'absolute',
  right: '0',
  top: '0',
  width: '48px',
  height: '46px',
  borderLeft: `1px solid ${token('color.border')}`,
});

const fadeGradientContainerStyles = xcss({
  position: 'absolute',
  top: '50px',
  right: '38px',
  width: '55px',
  height: '100%',
  background: `linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, ${token(
    'elevation.surface'
  )} 91.08%)`,
  pointerEvents: 'none',
});

export const DatabaseTableSkeletonRefresh = () => {
  return (
    <Box xcss={tableContainerStyles}>
      <Box xcss={tableHeaderStyles}>
        <Box xcss={[tableHeaderCellStyles, numberedEntryFieldStyles]} />
        {Array.from({ length: NUM_SKELETON_COLUMNS }).map((_, index) => (
          <Box
            key={`header-${index}`}
            xcss={[
              tableHeaderCellStyles,
              fieldStyles,
              index === NUM_SKELETON_COLUMNS - 1 && lastHeaderCellStyles,
            ]}
          >
            <Skeleton width="16px" height={DEFAULT_SKELETON_HEIGHT} isShimmering />
            <Skeleton width="80px" height={DEFAULT_SKELETON_HEIGHT} isShimmering />
          </Box>
        ))}
        <Box xcss={addFieldButtonContainerStyles} />
      </Box>

      <Box xcss={tableBodyStyles}>
        {Array.from({ length: NUM_SKELETON_ROWS }).map((_, rowIndex) => (
          <Box
            key={`row-${rowIndex}`}
            xcss={[
              tableRowStyles,
              rowIndex < NUM_SKELETON_ROWS - 1 && tableRowBorderStyles,
            ]}
          >
            <Box xcss={[tableCellStyles, numberedEntryFieldCellStyles]} />
            {Array.from({ length: NUM_SKELETON_COLUMNS }).map((_, cellIndex) => (
              <Box
                key={`cell-${rowIndex}-${cellIndex}`}
                xcss={[
                  tableCellStyles,
                  fieldCellStyles,
                  cellIndex === NUM_SKELETON_COLUMNS - 1 && lastCellStyles,
                ]}
              >
                <Skeleton width="100%" height={DEFAULT_SKELETON_HEIGHT} isShimmering />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box xcss={actionsContainerStyles}>
        <Box xcss={addEntryButtonSkeletonStyles}>
          <Skeleton width="83px" height={DEFAULT_SKELETON_HEIGHT} isShimmering />
        </Box>
      </Box>
      <Box xcss={fadeGradientContainerStyles} />
    </Box>
  );
};
